import createMapLibraryPlugin from 'libraries/mapLibrary.plugin';
import { epic$, store, store$ } from '@redux/store';
import LayoutBreakpoint from 'layout/enums/layoutBreakpoint';
import { combineLatest, distinctUntilChanged, map, shareReplay } from 'rxjs';
import createSearchResultsMapPlugin from './plugins/map.plugin';
import createMiniMapCardPlugin from './plugins/miniMapCard.plugin';
import createMarkerHighlightPlugin from './plugins/markerHighlight.plugin';
import { listing$, markerHighlight } from './services/listingFocus';
import createMapViewportPlugin from './plugins/mapViewport.plugin';
import createCarouselPlugin from './plugins/carousel.plugin';
import createSitePlanPlugin from './plugins/sitePlan.plugin';
import createMarkersPlugin from './plugins/markers.plugin';
import createInteractiveSitePlanPlugin from './plugins/interactiveSitePlan.plugin';
import {
  createStaticSitePlanClickHandler,
  createStaticSitePlanHoverHandler
} from './handlers/staticSitePlanHandlers';
import {
  createInteractiveSitePlanClickHandler,
  createInteractiveSitePlanHoverHandler,
} from './handlers/interactiveSitePlanHandlers';
import createSitePlanMiniCardPlugin from './plugins/sitePlanMiniCard.plugin';
import createSitePlanCarouselPlugin from './plugins/sitePlanCarousel.plugin';
import createPluginHost from './plugins/app';
import mapDataSlice, { DATA_SLICE_KEY } from './redux/dataSlice';
import mapInteractionSlice, { INTERACTION_SLICE_KEY } from './redux/interactionSlice';
import MarkerType from './enums/markerType';
import {
  selectFetchResultsStatus,
  selectMapLibraryStatus,
  selectMarkerCards,
} from './redux/selectors';
import createSearchWithinMapPlugin from './plugins/searchWithinMap.plugin';
import createBreakpointChecker from './services/createBreakpointChecker';
import { currentLocationProvided$ } from './redux/observables';
import { mapToggle$, signalMapToggle } from './services/mapToggle';
import createMapBoundariesPlugin from './plugins/mapBoundaries.plugin';
import createMarkerSelectionPlugin from './plugins/markerSelection.plugin';

const appRoot = document.querySelector('#nhs_map');
const mapSource = document.querySelector('[google-maps-source]')?.href;
const searchWithinMapButton = document.querySelector('[data-search-with-map-trigger]');

const onMapLoaded = () => {
  appRoot.classList.add('nhs-u-z-index--overlay-interactive');
}

const onMapLibraryFetch = () => {
  store.dispatch(mapDataSlice.actions.mapLibraryFetch());
};

const onMapLibraryLoaded = () => {
  store.dispatch(mapDataSlice.actions.mapLibraryLoaded());
};

const onMapLibraryError = (error) => {
  store.dispatch(
    mapDataSlice.actions.mapLibraryErrored(error?.message ?? 'Google Maps failed to load.')
  );
};

if (!mapSource) {
  throw new Error('Missing google maps source URL.');
}

if (!appRoot) {
  throw new Error('Missing #nhs_map container.');
}

const getBreakpoint = createBreakpointChecker(store.getState.bind(store));
const isDesktopBreakpoint = () => getBreakpoint() === LayoutBreakpoint.Big;

const isDesktop$ = store$.pipe(
  map(() => isDesktopBreakpoint()),
  distinctUntilChanged(),
);

/**
 * Observable that determines if the map should be visible based on breakpoint and toggle state.
 * On desktop/tablet the map is always visible; on mobile the user must toggle to map view.
 * Uses shareReplay so any plugin that subscribes after the initial emission gets the current state.
 */
const mapVisible$ = combineLatest({
  isDesktop: isDesktop$,
  isMapToggled: mapToggle$,
}).pipe(
  map(({ isDesktop, isMapToggled }) => isDesktop || isMapToggled),
  distinctUntilChanged(),
  shareReplay({ bufferSize: 1, refCount: false }),
);

const host = createPluginHost({
  root: appRoot,
  services: {
    epic$,
    store$,
    dispatch: store.dispatch.bind(store),
    getState: store.getState.bind(store),
    getBreakpoint,
    isDesktopBreakpoint,
  },
  plugins: [
    createMapLibraryPlugin({
      provider: 'google',
      config: {
        src: mapSource,
        onScriptInjected: onMapLibraryFetch,
        onLoad: onMapLibraryLoaded,
        onError: onMapLibraryError,
      },
    }),
    createSearchResultsMapPlugin({
      onMapLoaded
    }),
    createSearchWithinMapPlugin({
      triggerButton: searchWithinMapButton,
    }),
    createMapBoundariesPlugin(mapVisible$),
    createMapViewportPlugin(mapToggle$),
    createMarkersPlugin(),
    createMiniMapCardPlugin(),
    createMarkerHighlightPlugin(listing$),
    createCarouselPlugin(),
    createSitePlanPlugin({
      onSitePlanClick: createStaticSitePlanClickHandler(store.getState.bind(store), isDesktopBreakpoint),
      ...createStaticSitePlanHoverHandler(),
    }),
    createInteractiveSitePlanPlugin({
      onSitePlanClick: createInteractiveSitePlanClickHandler(store.getState.bind(store), isDesktopBreakpoint),
      ...createInteractiveSitePlanHoverHandler(),
    }),
    createSitePlanMiniCardPlugin(),
    createSitePlanCarouselPlugin(),
    createMarkerSelectionPlugin(),
  ],
});

store.injectReducer(DATA_SLICE_KEY, mapDataSlice.reducer);
store.injectReducer(INTERACTION_SLICE_KEY, mapInteractionSlice.reducer);

await host.init();
await host.start();

window.addEventListener('beforeunload', () => {
  host.stop();
});

export {
  MarkerType,
  selectFetchResultsStatus,
  selectMapLibraryStatus,
  selectMarkerCards,
  markerHighlight,
  currentLocationProvided$,
  signalMapToggle,
};
